import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Building2, Globe, Users, BadgeCheck, MapPin } from 'lucide-react';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

function About() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const parallaxOffset = -scrollY * 0.2;

  const stats = [
    { icon: <Users size={36} />, number: "20+", label: "Years Combined Experience" },
    { icon: <BadgeCheck size={36} />, number: "10+", label: "State Certifications" },
    { icon: <Users size={36} />, number: "12", label: "Team Members" },
    { icon: <Globe size={36} />, number: "2", label: "U.S. Regions Covered" }
  ];

  return (
    <div className="w-full overflow-hidden bg-gray-50">
      {/* Hero Section with Parallax */}
      <div className="relative h-screen overflow-hidden flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/assets/images/facade-hero.jpg')",
            transform: `translateY(${parallaxOffset}px)`,
            backgroundAttachment: "fixed"
          }}
        >
          <div className="absolute inset-0  bg-opacity-70"></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto text-white">
          <motion.h1
            className="text-5xl md:text-7xl font-bold mb-6 tracking-tight"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            The <span className='text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-cyan-300'>Facade</span> Connection
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-1 w-24 bg-sky-600 mx-auto mb-6"
          />

          <motion.p
            className="text-xl md:text-2xl italic font-light mb-8"
            style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            "A clear view of the glazing industry"
          </motion.p>


          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <a href="#our-story" className="inline-block px-8 py-3 bg-gradient-to-r from-sky-500 to-sky-500 text-white font-medium rounded-full hover:bg-teal-50 transition-colors shadow-lg">
              Discover Our Story
            </a>
          </motion.div>
        </div>

        <div className="absolute bottom-10 left-0 right-0 flex justify-center animate-bounce">
          <div className="w-8 h-8 border-r-2 border-b-2 border-white transform rotate-45"></div>
        </div>
      </div>

      {/* Company Introduction */}
      <section id="our-story" className="container mx-auto px-4 py-24">
        <motion.div
          className="flex flex-col md:flex-row gap-12 items-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          transition={{ staggerChildren: 0.2 }}
        >
          <motion.div
            className="md:w-1/2"
            variants={fadeIn}
            transition={{ duration: 0.7 }}
          >
            <span className="text-sky-600 font-semibold uppercase tracking-wider">Since 2015</span>
            <h2 className="text-4xl md:text-5xl font-bold text-indigo-900 mt-2 mb-6">Shaping the Future of Glazing Engineering</h2>
            <div className="h-1 w-24 bg-sky-600 mb-6"></div>
            <p className="text-lg text-gray-700 mb-8">
              At The Facade Connection, we blend over 20 years of industry experience with innovative,
              high-performance engineering solutions. Founded in 2020, our team has been shaping the
              glazing industry since 2015, delivering results that exceed expectations and ensure project success.
            </p>
            <p className="text-lg text-gray-700">
              Our mission is simple: to provide clear, actionable solutions that drive your project's success.
              Whether it's design, structural analysis, or expert consultation, we deliver the precision and
              reliability you need to keep projects on track and within budget.
            </p>
          </motion.div>

          <motion.div
            className="md:w-1/2 relative"
            variants={fadeIn}
            transition={{ duration: 0.7 }}
          >
            <div className="relative z-10 rounded-lg overflow-hidden shadow-2xl">
              <img src="/assets/images/facade-project.jpg" alt="Facade Engineering Project" className="w-full h-auto" />
            </div>
            <div className="absolute -z-10 top-8 -left-8 w-full h-full bg-teal-200 rounded-lg"></div>
          </motion.div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="bg-gradient-to-r from-orange-50 via-red-50 to-pink-50 py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            transition={{ staggerChildren: 0.1 }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center text-center text-sky-600"
                variants={fadeIn}
                transition={{ duration: 0.5 }}
              >
                <div className="mb-4 text-black">
                  {stat.icon}
                </div>
                <h3 className="text-4xl font-bold mb-2">{stat.number}</h3>
                <p className="text-black">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div> {/* 👈 Add this closing tag */}
      </section>


      {/* Our Vision & Mission */}
      <section className="container mx-auto px-4 py-24">
        <motion.div
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeIn}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-4xl font-bold text-indigo-900 mb-4">Our Vision & Core Values</h2>
          <div className="h-1 w-24 bg-sky-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            <span className="font-semibold">Engineering our client's road to success.</span> With a deep understanding
            of client needs, we tailor our services to ensure smooth execution from start to finish.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          transition={{ staggerChildren: 0.2 }}
        >
          <motion.div
            className="bg-white rounded-lg shadow-lg p-8 border-t-4 border-sky-600 hover:transform hover:scale-105 transition-transform duration-300"
            variants={fadeIn}
            transition={{ duration: 0.7 }}
          >
            <h3 className="text-2xl font-semibold text-indigo-900 mb-4">Expertise</h3>
            <p className="text-gray-600">
              Our team of architects, structural engineers, designers, and drafters brings specialized knowledge
              to every project, ensuring innovative solutions and exceptional results.
            </p>
          </motion.div>

          <motion.div
            className="bg-white rounded-lg shadow-lg p-8 border-t-4 border-sky-600 hover:transform hover:scale-105 transition-transform duration-300"
            variants={fadeIn}
            transition={{ duration: 0.7 }}
          >
            <h3 className="text-2xl font-semibold text-indigo-900 mb-4">Client Focus</h3>
            <p className="text-gray-600">
              We maintain an extremely client-oriented business culture, prioritizing your needs and
              ensuring clear communication throughout the project lifecycle.
            </p>
          </motion.div>

          <motion.div
            className="bg-white rounded-lg shadow-lg p-8 border-t-4 border-sky-600 hover:transform hover:scale-105 transition-transform duration-300"
            variants={fadeIn}
            transition={{ duration: 0.7 }}
          >
            <h3 className="text-2xl font-semibold text-indigo-900 mb-4">Innovation</h3>
            <p className="text-gray-600">
              We continually push the boundaries of what's possible in glazing engineering,
              embracing new technologies and approaches to solve complex challenges.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Team Section */}
      <section className="bg-gray-100 py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-4xl font-bold text-indigo-900 mb-4">Our Expertise</h2>
            <div className="h-1 w-24 bg-sky-600 mx-auto mb-6"></div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-xl overflow-hidden"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            transition={{ duration: 0.7 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-8 md:p-12">
                <div className="flex items-start mb-6">
                  <Building2 size={28} className="text-sky-600 mr-4 mt-1" />
                  <div>
                    <h3 className="text-2xl font-semibold text-indigo-900 mb-2">Comprehensive Team</h3>
                    <p className="text-gray-600">
                      Our 12-member team brings together architects, structural engineers, designers, and drafters
                      with diverse experience and expertise.
                    </p>
                  </div>
                </div>

                <div className="flex items-start mb-6">
                  <BadgeCheck size={28} className="text-sky-600 mr-4 mt-1" />
                  <div>
                    <h3 className="text-2xl font-semibold text-indigo-900 mb-2">Licensed Professionals</h3>
                    <p className="text-gray-600">
                      Our Professional Engineers are certified in more than 10 states, including Florida, New York,
                      New Jersey, Texas, Georgia, Pennsylvania, and Massachusetts.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <MapPin size={28} className="text-sky-600 mr-4 mt-1" />
                  <div>
                    <h3 className="text-2xl font-semibold text-indigo-900 mb-2">Regional Reach</h3>
                    <p className="text-gray-600">
                      Headquartered in Miami, Florida, we proudly serve clients throughout the southeast and central regions
                      of the United States.
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative min-h-full">
                <img src="/assets/images/team.jpg" alt="Team at work" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-indigo-900 to-teal-600">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center text-white"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Next Project?</h2>
            <p className="text-xl max-w-2xl mx-auto mb-8">
              Let our team of experts bring your vision to life with innovative solutions
              and unparalleled expertise in glazing engineering.
            </p>
            <a href="/contact" className="inline-block px-8 py-4 bg-white text-indigo-900 font-semibold rounded-full hover:bg-teal-50 transition-colors shadow-lg">
              Contact Our Team
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default About;